<template>
    <!-- Responsive Chat Box -->
    <div class="chat-box animate__animated animate__fadeInUp" :class="{ 'mobile-fullscreen': isMobile }"
        :style="chatBoxStyle">

        <!-- Chat Header -->
        <div class="chat-box-header">
            <div class="chat-user-info">
                <div class="chat-user-avatar-container">
                    <div class="chat-user-avatar">
                        {{ props.user.avatar }}
                    </div>
					<div v-if="userAvailability === 'active'" class="online-status-small"></div>
					<div v-else class="offline-status-small"></div>
                </div>

                <div class="chat-user-details">
                    <h3 class="chat-user-name">{{ props.user.name }}</h3>
					<p class="chat-user-status">{{ userStatusLabel }}</p>
                </div>
            </div>

            <div class="chat-actions">
                <!-- <button class="chat-action-btn" v-if="!isMobile">
                    <i class="fas fa-phone"></i>
                </button> -->
                <!-- <button class="chat-action-btn" v-if="!isMobile">
                    <i class="fas fa-video"></i>
                </button> -->
                <button class="chat-action-btn" v-if="!isMobile">
                    <i class="fas fa-ellipsis-v"></i>
                </button>
                <button class="chat-action-btn" @click="close">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </div>

        <!-- Messages Area -->
        <div class="messages-area" ref="messagesArea">
            <div v-for="message in messages" :key="message.id"
                :class="message.sender === senderKey ? 'message-item-sender message-sent' : 'message-item'">
                <div class="message-bubble" :class="message.sender === senderKey ? 'sent-bubble' : 'received-bubble'">
                    <p class="message-text">{{ message.text }}</p>
                    <p class="message-timestamp">{{ formatTime(message.timestamp) }}</p>
                </div>
                <div v-if="message.showSeen" class="message-status"
                    :style="{ marginLeft: message.sender === senderKey ? '0' : '10px', marginRight: message.sender === senderKey ? '10px' : '0' }">
                    <small class="text-primary">seen <i class="fas fa-check-double text-primary"
                            style="font-size:6px;"></i></small>
                </div>
            </div>
        </div>

        <!-- Message Input -->
        <div class="message-input-area">
            <div class="input-wrapper">
                <input type="text" v-model="newMessage" @keypress="handleKeyPress" placeholder="Type a message..."
                    class="message-input" />
                <button @click="sendMessage" :disabled="!newMessage.trim()" class="send-button">
                    <i class="fas fa-paper-plane"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { useStore } from 'vuex';
import { ref, onMounted, computed, onUnmounted, watch, nextTick } from 'vue';
import 'animate.css';
import { sendMessageToFirebase, findChatsForUser, markChatAsRead } from '@/js/utils/firebase';
import { ref as dbRef, getDatabase, onValue } from 'firebase/database';

const props = defineProps({
    user: Object,
    index: Number
});

const store = useStore();
const messages = ref([]);
const newMessage = ref('');
const windowWidth = ref(window.innerWidth);
const messagesArea = ref(null);
const isUserOnline = ref(false);
const userAvailability = ref('offline');

const userStatusLabel = computed(() => {
	if (userAvailability.value === 'active') return 'Active now';
	return 'Offline';
});

// Responsive breakpoints
const isMobile = computed(() => windowWidth.value < 768);
const isTablet = computed(() => windowWidth.value >= 768 && windowWidth.value < 1024);
const isDesktop = computed(() => windowWidth.value >= 1024);


const currentUser = store.getters.user.user.role?.id;
const currentRole = store.getters.role.split('-')[0];
const userTypeMap = { staff: 'staffs', customer: 'customers', owner: 'owner' };
const senderKey = `${userTypeMap[currentRole] || currentRole}-${currentUser}`;


// Dynamic chat box styling
const chatBoxStyle = computed(() => {
    const openChatsLength = store.getters['openChats'].length;

    if (isMobile.value) {
        return {
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            width: '100%',
            height: '100vh',
            zIndex: 1050
        };
    } else if (isTablet.value) {
        return {
            position: 'fixed',
            right: `${20 + 280 * (props.index % 2)}px`,
            bottom: '0px',
            width: '260px',
            height: '400px',
            zIndex: 1050
        };
    } else {
        return {
            position: 'fixed',
            right: `${openChatsLength == 0 ? 530 : 20 + 320 * (props.index % 3)}px`,
            bottom: '0px',
            width: '300px',
            height: '500px',
            zIndex: 1050
        };
    }
});

// Handle window resize
const handleResize = () => {
    windowWidth.value = window.innerWidth;
};

const sendMessage = () => {
    const currentUser = store.getters.user.user;
    const brandId = store.getters.brand?.id;
    let roleType = store.getters.role.split('-')[0];
    const typeMap = {
        'staff': 'staffs',
        'owner': 'owner',  // no change
        'customer': 'customers'
    };
    const sender = {
        type: typeMap[roleType.split('-')[0]] || roleType.split('-')[0],
        id: currentUser.role?.id,          // numeric or string ID
    };
    if (!newMessage.value.trim() || !props.user) return;

    const now = Date.now();
    const message = {
        id: Date.now(),
        text: newMessage.value,
        sender: senderKey,
        timestamp: now,
    };
    
    if (!messages.value) {
        messages.value = [];
    }

    // messages.value.push(message);
     // newMessage.value = '';
    markMessagesAsRead();

    sendMessageToFirebase({
        brandID: brandId,
        sender: sender,
        recipient: props.user,
        messageText: newMessage.value
    });

    newMessage.value = '';


};

const markMessagesAsRead = () => {
    const type = store.getters.role.split('-')[0];
    const id = store.getters.user.user.role?.id;
    const mappedType = type === 'staff' ? 'staffs' : type === 'customer' ? 'customers' : type;
    const userKey = `${mappedType}-${id}`;

    if (props.user) {
        const targetUserKey = `${props.user.type}-${props.user.id}`;
        const currentUserKey = userKey;

        // Generate the same chatId format as in your existing code
        const participants = [currentUserKey, targetUserKey].sort();
        const chatId = `${participants[0]}-${participants[1]}`;
        const brandId = store.getters.brand?.id;

        markChatAsRead(brandId, chatId, userKey);
    }
};

const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
    }
};

const close = () => {
    store.dispatch('closeChat', props.user);
};

// Initialize
onMounted(() => {
    messages.value = props.user.messages || [];
    const brandId = store.getters.brand?.id;
    window.addEventListener('resize', handleResize);

    markMessagesAsRead();


    const type = store.getters.role.split('-')[0];
    const id = store.getters.user.user.role?.id;
    const userKey = `${type === 'staff' ? 'staffs' : type === 'customer' ? 'customers' : type}-${id}`;
    
    findChatsForUser(brandId, userKey, (chatList) => {
        
        // Optional: auto-open first chat if desired
        if (chatList.length) {
            const firstChat = chatList[0];

            const currentUserKey = senderKey; // already computed
            const targetUserKey = `${props.user.type}-${props.user.id}`;
            
            const targetChatId1 = `${currentUserKey}-${targetUserKey}`;
            const targetChatId2 = `${targetUserKey}-${currentUserKey}`;

            const relevantChat = chatList.find(chat =>
                chat.chatId === targetChatId1 || chat.chatId === targetChatId2
            );

            // Optional: ensure both users are participants
            if (
                relevantChat &&
                relevantChat.chatData.participants &&
                relevantChat.chatData.participants[currentUserKey] &&
                relevantChat.chatData.participants[targetUserKey]
            ) {
                const allMessages = Object.entries(relevantChat.chatData.messages || {}).map(([id, msg]) => ({
                    id,
                    ...msg
                }));

                const recipientKey = `${props.user.type}-${props.user.id}`;
                // Find the latest message sent by current user
                const lastSentByMe = [...allMessages]
                    .filter(msg => msg.sender === senderKey)
                    .sort((a, b) => b.timestamp - a.timestamp)[0];

                messages.value = allMessages.map(msg => ({
                    ...msg,
                    showSeen: msg.id === lastSentByMe?.id && lastSentByMe.readBy?.[recipientKey] === true
                }));
            } else {
                // Clear if no chat exists between them
                messages.value = [];
            }
        }
    });

    if (messagesArea.value) {
        messagesArea.value.addEventListener('scroll', handleScroll);
    }
});


watch(() => props.user, async (newUser) => {
    if (!newUser) return;

    messages.value = []; // ✅ clear previous messages first

    const type = store.getters.role.split('-')[0];
    const id = store.getters.user.user.role?.id;
    const userKey = `${type === 'staff' ? 'staffs' : type === 'customer' ? 'customers' : type}-${id}`;
    const brandId = store.getters.brand?.id;

    findChatsForUser(brandId, userKey, (chatList) => {
        const targetChatId1 = `${senderKey}-${newUser.type}-${newUser.id}`;
        const targetChatId2 = `${newUser.type}-${newUser.id}-${senderKey}`;

        const relevantChat = chatList.find(chat =>
            chat.chatId === targetChatId1 || chat.chatId === targetChatId2
        );

        if (relevantChat) {
            messages.value = Object.entries(relevantChat.chatData.messages || {}).map(([id, msg]) => ({
                id,
                ...msg
            }));
        }
    });
}, { immediate: true });

watch(messages, () => {
    nextTick(() => {
        scrollToBottom();
    });
});

watch(() => props.user, (newUser) => {
    if (newUser) {
        // Small delay to ensure the chat is fully loaded
        setTimeout(() => {
            markMessagesAsRead();
        }, 500);
    }
}, { immediate: true });

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    if (messagesArea.value) {
        messagesArea.value.removeEventListener('scroll', handleScroll);
    }
});


//watch for online status changes
watch(() => props.user, (newUser) => {
    if (!newUser) return;

    const userKey = `${newUser.type}-${newUser.id}`;
    const brandId = store.getters.brand?.id;
    const statusRef = dbRef(getDatabase(), `brand-${brandId}/users/${userKey}`);

    onValue(statusRef, (snapshot) => {
		const presence = snapshot.val() || {};
		userAvailability.value = presence.availability === 'active' && presence.isOnline === true
			? 'active'
			: 'offline';
        isUserOnline.value = userAvailability.value === 'active';
    });
}, { immediate: true });


const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

const scrollToBottom = () => {
    if (messagesArea.value) {
        // Smooth scroll to bottom
        messagesArea.value.scrollTop = messagesArea.value.scrollHeight;
    }
};


const handleScroll = () => {
    if (messagesArea.value) {
        const { scrollTop, scrollHeight, clientHeight } = messagesArea.value;
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

        if (isAtBottom) {
            markMessagesAsRead();
        }
    }
};

</script>

<style scoped>
/* Base Chat Box Styles */
.chat-box {
    display: flex;
    flex-direction: column;
    background: #FFFFFF;
    border-radius: 5px 5px 0 0;
    box-shadow: 0 -4px 32px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    transition: all 0.3s ease;
}

/* Mobile Fullscreen Mode */
.mobile-fullscreen {
    border-radius: 0;
    box-shadow: none;
}

/* Chat Header */
.chat-box-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
    background: white;
    min-height: 80px;
}

.chat-user-info {
    display: flex;
    align-items: center;
    flex: 1;
}

.chat-user-avatar-container {
    position: relative;
    margin-right: 10px;
}

.chat-user-avatar {
    width: 38px;
    height: 38px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 14px;
}

.online-status-small {
    position: absolute;
    bottom: 1px;
    right: 1px;
    width: 10px;
    height: 10px;
    background-color: #28a745;
    border: 2px solid white;
    border-radius: 50%;
}

.offline-status-small {
    position: absolute;
    bottom: 1px;
    right: 1px;
    width: 10px;
    height: 10px;
    background-color: #7e7c7c;
    border: 2px solid white;
    border-radius: 50%;
}


.chat-user-details {
    flex: 1;
}

.chat-user-name {
    font-weight: 600;
    color: #333;
    font-size: 14px;
    margin: 0 0 2px 0;
}

.chat-user-status {
    font-size: 11px;
    color: #666;
    margin: 0;
}

.chat-actions {
    display: flex;
    gap: 4px;
}

.chat-action-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    border-radius: 25%;
    transition: background-color 0.2s;
    color: #666;
    min-width: 32px;
    height: 32px;
}

.chat-action-btn:hover {
    background-color: #f0f0f0;
}

/* Messages Area */
.messages-area {
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    background-color: #fafafa;
    min-height: 0;
}

.message-item {
    margin-bottom: 12px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.message-item-sender {
    margin-bottom: 12px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

.message-bubble {
    max-width: 75%;
    padding: 10px 14px;
    border-radius: 16px;
    word-wrap: break-word;
    word-break: break-word;
}

.sent-bubble {
    background-color: #007bff;
    color: white;
}

.received-bubble {
    background-color: #e9ecef;
    color: #333;
}

.message-status {
    font-size: 0.7em;
    margin-top: 2px;
}

.message-text {
    font-size: 14px;
    margin: 0 0 4px 0;
    line-height: 1.4;
}

.message-timestamp {
    font-size: 10px;
    margin: 0;
    opacity: 0.7;
}

/* Message Input */
.message-input-area {
    padding: 12px 16px;
    border-top: 1px solid #eee;
    background: white;
}

.input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.message-input {
    flex: 1;
    padding: 10px 16px;
    background-color: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    outline: none;
    font-size: 14px;
    transition: all 0.3s ease;
}

.message-input:focus {
    border-color: #007bff;
    background-color: #fff;
}

.send-button {
    background-color: #007bff;
    color: white;
    border: none;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    cursor: pointer;
    transition: background-color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.send-button:hover:not(:disabled) {
    background-color: #0056b3;
}

.send-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Responsive Design */
@media (max-width: 767px) {
    .chat-box-header {
        padding: 16px;
        min-height: 80px;
    }

    .chat-user-avatar {
        width: 42px;
        height: 42px;
        font-size: 16px;
    }

    .chat-user-name {
        font-size: 16px;
    }

    .chat-user-status {
        font-size: 12px;
    }

    .messages-area {
        padding: 12px;
    }

    .message-bubble {
        max-width: 85%;
        padding: 12px 16px;
    }

    .message-text {
        font-size: 15px;
    }

    .message-input {
        padding: 12px 18px;
        font-size: 15px;
    }

    .send-button {
        width: 40px;
        height: 40px;
    }
}

@media (max-width: 480px) {
    .message-bubble {
        max-width: 90%;
    }

    .chat-actions {
        gap: 2px;
    }

    .chat-action-btn {
        padding: 6px;
        min-width: 28px;
        height: 28px;
    }
}

@media (min-width: 768px) and (max-width: 1023px) {
    .chat-box {
        width: 260px !important;
        height: 400px !important;
    }

    .messages-area {
        padding: 12px;
    }

    .message-bubble {
        max-width: 80%;
    }
}

@media (min-width: 1024px) {
    .chat-box {
        width: 300px !important;
        height: 500px !important;
    }
}

/* Landscape mobile orientation */
@media (max-width: 767px) and (orientation: landscape) {
    .chat-box-header {
        min-height: 60px;
        padding: 12px 16px;
    }

    .chat-user-avatar {
        width: 36px;
        height: 36px;
        font-size: 14px;
    }

    .message-input-area {
        padding: 10px 16px;
    }
}
</style>
